/**
 * Identifiers — UUIDv7, minted by the writer (ADR-0010 §6).
 *
 * Why v7 rather than v4: the clinical record is append-only and correction is a
 * new row, so readers constantly sort by creation. v7 embeds a millisecond
 * timestamp in its leading 48 bits, which makes IDs k-sortable and keeps
 * database index locality sane under insert-only writes.
 *
 * Why minted by the writer: the physio device generates event IDs offline, with
 * no server round-trip, so a crash-and-retry re-sync is idempotent by INSERT
 * rather than by upsert — upsert is forbidden, since UPDATE/DELETE are revoked
 * at the database level (ADR-0010 §1).
 */

/** Nominal typing: a SessionId cannot be passed where an EpisodeId is wanted. */
declare const brand: unique symbol;
type Brand<T, B> = T & { readonly [brand]: B };

export type Uuid = Brand<string, 'Uuid'>;
export type PatientId = Brand<string, 'PatientId'>;
export type EpisodeId = Brand<string, 'EpisodeId'>;
export type SessionId = Brand<string, 'SessionId'>;

/**
 * Source of cryptographic randomness. Defaults to the Web Crypto API, which is
 * present on Node 19+, every target browser, and React Native's Hermes runtime
 * once a polyfill is registered. Injectable so tests can be deterministic.
 */
export type RandomSource = (bytes: Uint8Array) => Uint8Array;

/**
 * Structural, not `lib.dom`'s `Crypto`. This package carries no DOM lib — it is
 * consumed by React Native as well as the browser and Node — so the contract is
 * expressed as the one method actually needed.
 */
interface RandomValuesProvider {
  getRandomValues<T extends ArrayBufferView>(array: T): T;
}

const webCryptoRandom: RandomSource = (bytes) => {
  const c = (globalThis as { crypto?: RandomValuesProvider }).crypto;
  if (!c?.getRandomValues) {
    // Deliberately fatal. Falling back to Math.random would silently weaken the
    // identifiers underpinning an audit trail — a clinical-record defect that
    // would not surface until someone went looking for it.
    throw new Error(
      'crypto.getRandomValues is unavailable. Register a Web Crypto polyfill ' +
        'before minting identifiers, or pass an explicit RandomSource.',
    );
  }
  return c.getRandomValues(bytes);
};

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

/**
 * Mint a UUIDv7 (RFC 9562 §5.7).
 *
 * Layout: 48 bits unix_ts_ms | 4 bits version | 12 bits rand_a
 *         | 2 bits variant | 62 bits rand_b
 *
 * Note: IDs minted within the same millisecond sort arbitrarily against each
 * other — this implementation does not add the optional monotonic counter.
 * Ordering between distinct milliseconds is guaranteed, which is what the
 * record needs; exact intra-millisecond order is carried by the event's own
 * tap timestamp (ADR-0010 §3), not by the ID.
 */
export function uuidv7(now: number = Date.now(), random: RandomSource = webCryptoRandom): Uuid {
  const bytes = new Uint8Array(16);
  // A byteOffset-6 view, not a fresh zero-offset buffer: expo-crypto's Android
  // binding computes its native pointer as base+byteOffset, then separately
  // re-applies byteOffset as a write position — a non-zero offset gets applied
  // twice and overruns the allocation. Every RandomSource must see offset 0.
  const entropy = new Uint8Array(10);
  random(entropy);
  bytes.set(entropy, 6);

  // 48-bit big-endian timestamp. Split at 32 bits so the arithmetic stays
  // inside the safe integer range without BigInt.
  const view = new DataView(bytes.buffer);
  view.setUint16(0, Math.floor(now / 0x1_0000_0000));
  view.setUint32(2, now >>> 0);

  // Version 7 into the high nibble of byte 6; RFC 4122 variant into byte 8.
  view.setUint8(6, (view.getUint8(6) & 0x0f) | 0x70);
  view.setUint8(8, (view.getUint8(8) & 0x3f) | 0x80);

  let hex = '';
  for (const b of bytes) hex += HEX[b];

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` as Uuid;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: string): value is Uuid {
  return UUID_RE.test(value);
}

/** Recover the mint time from a v7 identifier. Returns null for other versions. */
export function uuidv7Timestamp(id: string): Date | null {
  if (!isUuid(id) || id[14] !== '7') return null;
  return new Date(parseInt(id.slice(0, 8) + id.slice(9, 13), 16));
}

export const newPatientId = (): PatientId => uuidv7() as string as PatientId;
export const newEpisodeId = (): EpisodeId => uuidv7() as string as EpisodeId;
export const newSessionId = (): SessionId => uuidv7() as string as SessionId;
